import React from 'react';
import { colors } from '../../utils/theme';
import { fontFamily, radius, spacing, shadow, typography } from '../../theme/tokens';

// AppCrashFallback — what the user sees when the root Sentry.ErrorBoundary catches
// an unrecoverable render error.
//
// WHY: `src/index.js` passed a BARE STRING (`fallback={'An error has occurred'}`),
// so a crash painted unstyled black serif-default text in the top-left corner of a
// white page, with no branding, no explanation, and — the part that actually costs
// the user their session — NO WAY TO RECOVER. Reloading was something they had to
// think of themselves. MEASURED on the deployed staging bundle 2026-07-28: the
// literal `An error has occurred` is present in `static/js/main.<hash>.js`.
//
// This component is deliberately DEPENDENCY-LIGHT. It renders only after the app
// has already thrown, so it must not be able to throw itself: no router hooks, no
// redux, no data layer, no MUI components, no images. Only the design tokens
// (plain objects) and inline styles. Navigation uses `window.location` rather than
// react-router, because the router is part of what may have just died.
//
// Design: matches the app's existing shipped error treatment (the text-only
// "We couldn't load your notifications." / "This wheel isn't available." states) —
// tokenised type, canonical brand purple primary action, no invented hexes and no
// new illustration. The designed <ErrorWheel> mark (red-hued Wheel + exclamation,
// error-state-wheel-mark-spec) is a separate, still-unbuilt shared component whose
// own spec says it must REPLACE the existing scattered error images rather than add
// another one — when it lands, it drops in here too.
function AppCrashFallback() {
  const reload = () => {
    // Full reload, not a soft re-render: the store/route that produced the crash
    // is exactly the state we want to discard.
    window.location.reload();
  };

  const goHome = () => {
    // stgfix-0927-P0-kelly-ai-wheel-no-segments-edit-error-goes-discover:
    // '/' (PATH.DASHBOARD) redirects an authenticated user to /discover
    // (routes.js), not their gallery -- so this "Go to my gallery" button
    // landed on Discover instead. '/gallery' (GalleryRoute) redirects a
    // logged-in visitor with no :id to their own /gallery/:userId, and an
    // anonymous one to /login -- matching the button's label either way.
    window.location.assign('/gallery');
  };

  const buttonBase = {
    fontFamily,
    fontSize: typography.button.fontSize,
    fontWeight: typography.button.fontWeight,
    lineHeight: 1,
    // >= 44px tall / wide enough for a comfortable touch target on every viewport.
    minHeight: 44,
    padding: `${spacing.md}px ${spacing.xl}px`,
    borderRadius: radius.sm,
    cursor: 'pointer',
  };

  return (
    <div
      role="alert"
      aria-live="assertive"
      style={{
        fontFamily,
        // Centred in the VIEWPORT, not in a shrink-wrapped box.
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: spacing.lg,
        padding: spacing.xl,
        boxSizing: 'border-box',
        textAlign: 'center',
        backgroundColor: colors.background,
        color: colors.primary,
      }}
    >
      <div
        style={{
          maxWidth: 480,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: spacing.md,
          padding: spacing.xl,
          boxSizing: 'border-box',
          backgroundColor: colors.white,
          borderRadius: radius.md,
          boxShadow: shadow.e2,
        }}
      >
        <h1
          style={{
            margin: 0,
            fontSize: typography.header3.fontSize,
            fontWeight: typography.header3.fontWeight,
            lineHeight: typography.header3.lineHeight,
            color: colors.primary,
          }}
        >
          Something went wrong
        </h1>

        <p
          style={{
            margin: 0,
            fontSize: typography.largeText.fontSize,
            fontWeight: typography.largeText.fontWeight,
            lineHeight: typography.largeText.lineHeight,
            color: colors.darkGrey,
          }}
        >
          iYuga ran into an unexpected error and couldn&apos;t finish loading this
          page. Reloading usually fixes it — nothing you&apos;ve saved has been lost.
        </p>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: spacing.md,
            marginTop: spacing.sm,
          }}
        >
          <button
            type="button"
            onClick={reload}
            style={{
              ...buttonBase,
              backgroundColor: colors.PrimaryPurple,
              color: colors.white,
              border: `1px solid ${colors.PrimaryPurple}`,
            }}
          >
            Reload the page
          </button>
          <button
            type="button"
            onClick={goHome}
            style={{
              ...buttonBase,
              backgroundColor: colors.white,
              color: colors.primary,
              border: `1px solid ${colors.lightBorder}`,
            }}
          >
            Go to my gallery
          </button>
        </div>
      </div>
    </div>
  );
}

export default AppCrashFallback;
